import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from '@c2fo/liquidity';
import { paths } from '@/generated/supplier-experience-api';
import apiClient from '@/lib/apiClient';
import queryClient from '@/lib/queryClient';
import useDataDog from '@/reporting/DataDog';
import type { UserPreferences } from '@/shared/data/useUserPreferences';

type AutoCreateOffersForNewBuyersPostBody =
  paths['/user/preferences']['patch']['requestBody']['content']['application/json'];

const useUpdateIncludeNewBuyer = () => {
  const { t } = useTranslation();
  const { addError } = useDataDog();
  const showSnackbar = useSnackbar();

  return useMutation({
    mutationKey: ['updateIncludeNewBuyer'],
    mutationFn: (autoCreateOffersForNewBuyers: boolean) => {
      return apiClient.patch(`api/supplier-experience/user/preferences`, {
        json: {
          features: {
            autoCreateOffersForNewBuyers,
          },
        } satisfies AutoCreateOffersForNewBuyersPostBody,
      });
    },
    onSuccess: (_data, newValue) => {
      // using setQueriesData to optmistically update since user-preferences query key is a compound key
      // 'user-preferences, true' = query ran
      // 'user-preferences, false' = query not ran because we short circuit to prevent over fetching from offer builder for one time offers
      queryClient.setQueriesData<UserPreferences>({ queryKey: ['user-preferences'] }, (prev) => {
        if (!prev) {
          return prev;
        }

        return {
          ...prev,
          autoCreateOffersForNewBuyers: newValue,
        };
      });

      showSnackbar({ message: t('accountSettings.preferenceUpdated') });
    },
    onError: (error, newValue) => {
      addError('Enterprise Include New Buyer Update Error', {
        error,
        location: 'useUpdateIncludeNewBuyer hook',
        autoCreateOffersForNewBuyers: newValue,
      });

      showSnackbar({ message: t('accountSettings.preferenceUpdateError') });
    },
    onSettled: () => {
      queryClient.refetchQueries({ queryKey: ['user-preferences'] });
    },
  });
};

export default useUpdateIncludeNewBuyer;
